/**
 * @file api/tokens.js
 * @description API functions for the /api/tokens resource.
 * All functions return the parsed response data directly.
 */

import apiClient from './client';

/**
 * Fetches all API tokens (metadata only — no hashes or plaintext values).
 * @returns {Promise<Array>} Array of token objects.
 */
export const listTokens = async () => {
  const res = await apiClient.get('/api/tokens');
  return res.data;
};

/**
 * Fetches a single API token by ID.
 * @param {number} id - Token ID.
 * @returns {Promise<Object>} Token metadata.
 */
export const getToken = async (id) => {
  const res = await apiClient.get(`/api/tokens/${id}`);
  return res.data;
};

/**
 * Creates a new API token.
 * The response includes the full plaintext `token` value exactly once.
 *
 * @param {{ name: string, description?: string, scopes?: string[], expires_at?: string|null }} payload
 * @returns {Promise<Object>} Created token with the one-time plaintext value.
 */
export const createToken = async (payload) => {
  const res = await apiClient.post('/api/tokens', payload);
  return res.data;
};

/**
 * Updates metadata for an existing token.
 * @param {number} id - Token ID.
 * @param {{ name?: string, description?: string, scopes?: string[], expires_at?: string|null }} payload
 * @returns {Promise<Object>} Updated token metadata.
 */
export const updateToken = async (id, payload) => {
  const res = await apiClient.put(`/api/tokens/${id}`, payload);
  return res.data;
};

/**
 * Regenerates a token's secret value. The old value is immediately invalidated.
 * The response includes the new plaintext `token` value exactly once.
 *
 * @param {number} id - Token ID.
 * @returns {Promise<Object>} Token metadata with the new one-time plaintext value.
 */
export const regenerateToken = async (id) => {
  const res = await apiClient.post(`/api/tokens/${id}/regenerate`);
  return res.data;
};

/**
 * Revokes (soft-deletes) a token by ID.
 * @param {number} id - Token ID.
 * @returns {Promise<Object>} Confirmation message.
 */
export const revokeToken = async (id) => {
  const res = await apiClient.delete(`/api/tokens/${id}`);
  return res.data;
};
